import React, { useState, useEffect, useCallback } from 'react';
import { ref, get, update, remove } from 'firebase/database';
import { db } from './firebase-config';
import './NewsletterSubscribers.css';

function NewsletterSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Load subscribers from database
  const loadSubscribers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const subscribersRef = ref(db, 'newsletter_subscribers');
      const snapshot = await get(subscribersRef);
      
      if (snapshot.exists()) {
        const subscribersData = snapshot.val();
        const subscribersList = Object.keys(subscribersData).map(key => ({
          id: key,
          ...subscribersData[key]
        }));
        
        // Sort by subscription date (newest first)
        subscribersList.sort((a, b) => 
          new Date(b.subscribedAt) - new Date(a.subscribedAt)
        );
        
        setSubscribers(subscribersList);
      } else {
        setSubscribers([]);
      }
    } catch (err) {
      console.error('Error loading subscribers:', err);
      setError('Failed to load subscribers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load subscribers on component mount
  useEffect(() => {
    loadSubscribers();
  }, [loadSubscribers]);

  // Toggle subscriber active status
  const toggleSubscriberStatus = async (subscriber) => {
    try {
      const newStatus = !subscriber.active;
      await update(ref(db, `newsletter_subscribers/${subscriber.id}`), {
        active: newStatus
      });
      
      // Update local state
      setSubscribers(prev => 
        prev.map(sub => 
          sub.id === subscriber.id ? { ...sub, active: newStatus } : sub
        )
      );
      
      setSuccess(`Subscriber ${newStatus ? 'activated' : 'deactivated'} successfully.`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating subscriber status:', err);
      setError('Failed to update subscriber status. Please try again.');
      
      // Clear error message after 3 seconds
      setTimeout(() => setError(''), 3000);
    }
  };

  // Delete subscriber
  const deleteSubscriber = async (subscriber) => {
    if (window.confirm(`Are you sure you want to delete ${subscriber.email}?`)) {
      try {
        await remove(ref(db, `newsletter_subscribers/${subscriber.id}`));
        
        // Update local state
        setSubscribers(prev => prev.filter(sub => sub.id !== subscriber.id));
        
        setSuccess('Subscriber deleted successfully.');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error deleting subscriber:', err);
        setError('Failed to delete subscriber. Please try again.');
        
        // Clear error message after 3 seconds
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Filter subscribers
  const filteredSubscribers = subscribers.filter(subscriber => {
    if (filter === 'all') return true;
    if (filter === 'active') return subscriber.active;
    if (filter === 'inactive') return !subscriber.active;
    return true;
  });

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="newsletter-subscribers">
      <h2 className="newsletter-subscribers__title">Newsletter Subscribers</h2>
      
      <div className="newsletter-subscribers__filters">
        <button 
          className={`newsletter-subscribers__filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({subscribers.length})
        </button>
        <button 
          className={`newsletter-subscribers__filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Active ({subscribers.filter(s => s.active).length})
        </button>
        <button 
          className={`newsletter-subscribers__filter-btn ${filter === 'inactive' ? 'active' : ''}`}
          onClick={() => setFilter('inactive')}
        >
          Inactive ({subscribers.filter(s => !s.active).length})
        </button>
      </div>
      
      {error && <p className="newsletter-subscribers__error">{error}</p>}
      {success && <p className="newsletter-subscribers__success">{success}</p>}
      
      {isLoading ? (
        <p className="newsletter-subscribers__loading">Loading subscribers...</p>
      ) : filteredSubscribers.length > 0 ? (
        <div className="newsletter-subscribers__table-container">
          <table className="newsletter-subscribers__table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Subscribed On</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscribers.map(subscriber => (
                <tr key={subscriber.id} className={subscriber.active ? 'active' : 'inactive'}>
                  <td>{subscriber.email}</td>
                  <td>{formatDate(subscriber.subscribedAt)}</td>
                  <td>
                    <span className={`status-badge ${subscriber.active ? 'active' : 'inactive'}`}>
                      {subscriber.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="newsletter-subscribers__actions">
                    <button 
                      className="newsletter-subscribers__action-btn toggle"
                      onClick={() => toggleSubscriberStatus(subscriber)}
                      title={subscriber.active ? 'Deactivate' : 'Activate'}
                    >
                      {subscriber.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button 
                      className="newsletter-subscribers__action-btn delete"
                      onClick={() => deleteSubscriber(subscriber)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="newsletter-subscribers__empty">No subscribers found.</p>
      )}
    </div>
  );
}

export default NewsletterSubscribers; 